import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Badge from '@/components/layout/Badge';
import Modal from '@/components/layout/Modal';
import Icon from '@mdi/react';
import {
    mdiArrowDown,
    mdiArrowUp,
    mdiCancel,
    mdiCheckCircle,
    mdiCheckCircleOutline,
    mdiClose,
    mdiLoading,
    mdiPencil,
    mdiPlus,
    mdiToggleSwitch,
    mdiToggleSwitchOffOutline,
    mdiTrashCan,
} from '@mdi/js';
import { toastError, toastSuccess } from '@/lib/toast';
import { queryClient } from '@/queryClient';
import { extractApiError } from '@/lib/apiError';
import { PAISES_OPTIONS, rotuloPais } from '@/lib/paises';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import CampoNumero from '@/components/Formulario/CampoNumero';
import type {
    InboundDefaultAction,
    InboundPolicyDTO,
    InboundPolicyScope,
    InboundRuleAction,
    InboundRuleDTO,
    InboundRuleType,
    InboundServiceDTO,
} from '@webip/shared/src/dto/firewall_entrada';
import type { ApplianceDTO } from '@webip/shared/src/dto/appliance';
import type { FeedDTO } from '@webip/shared/src/dto/firewall';

// ─── OPÇÕES ───────────────────────────────────────────────────────────────────

const SCOPE_OPTIONS = [
    { id: 'global', label: 'Global (todos os serviços de todas as appliances)' },
    { id: 'appliance', label: 'Appliance (todos os serviços dela)' },
    { id: 'servicos', label: 'Serviços escolhidos' },
];

const DEFAULT_ACTION_OPTIONS = [
    { id: 'allow', label: <HBox style={{ justifyContent: 'center' }}><Icon path={mdiCheckCircleOutline} size={0.8} color="var(--tc-color-success)" /> Seguir para a próxima política (libera se for a última)</HBox> },
    { id: 'deny', label: <HBox style={{ justifyContent: 'center' }}><Icon path={mdiCancel} size={0.8} color="var(--tc-color-danger)" /> Bloquear</HBox> },
];

const RULE_ACTION_OPTIONS = [
    { id: 'allow', label: <HBox><Icon path={mdiCheckCircleOutline} size={0.8} color="var(--tc-color-success)" /> Permitir</HBox> },
    { id: 'block', label: <HBox><Icon path={mdiCancel} size={0.8} color="var(--tc-color-danger)" /> Bloquear</HBox> },
];

const RULE_TYPE_OPTIONS = [
    { id: 'country', label: 'País' },
    { id: 'cidr', label: 'IP / Rede (CIDR)' },
    { id: 'asn', label: 'ASN' },
    { id: 'feed', label: 'Feed de IP' },
];

// ─── FORMULÁRIO ───────────────────────────────────────────────────────────────

type RegraForm = {
    id?: number;
    type: InboundRuleType;
    target: string;
    action: InboundRuleAction;
    feedId: string | null;
};

type Form = {
    name: string;
    description: string;
    scope: InboundPolicyScope;
    applianceId: string | null;
    services: string[]; // "redirecionamento:7" | "vpn:3"
    priority: number;
    enabled: boolean;
    defaultAction: InboundDefaultAction;
    rules: RegraForm[];
};

const FORM_VAZIO: Form = {
    name: '',
    description: '',
    scope: 'servicos',
    applianceId: null,
    services: [],
    priority: 10,
    enabled: true,
    defaultAction: 'deny',
    rules: [],
};

function formDe(p: InboundPolicyDTO): Form {
    return {
        name: p.name,
        description: p.description,
        scope: p.scope,
        applianceId: p.applianceId != null ? String(p.applianceId) : null,
        services: p.services.map((s) => `${s.tipo}:${s.id}`),
        priority: p.priority,
        enabled: p.enabled,
        defaultAction: p.defaultAction,
        rules: p.rules.map((r) => ({
            id: r.id,
            type: r.type,
            target: r.target,
            action: r.action,
            feedId: r.feedId != null ? String(r.feedId) : null,
        })),
    };
}

function validar(form: Form): string | null {
    if (!form.name.trim()) return 'Nome é obrigatório';
    if (form.scope === 'appliance' && !form.applianceId) return 'Escolha a appliance';
    if (form.scope === 'servicos' && form.services.length === 0) return 'Escolha ao menos um serviço';
    for (let i = 0; i < form.rules.length; i++) {
        const r = form.rules[i];
        if (r.type === 'feed' ? !r.feedId : !r.target.trim()) return `Regra ${i + 1}: informe a origem`;
    }
    return null;
}

/** Texto curto da regra para a tabela. */
function descreverRegra(r: InboundRuleDTO): string {
    const acao = r.action === 'allow' ? 'Permitir' : 'Bloquear';
    const alvo =
        r.type === 'country' ? rotuloPais(r.target)
            : r.type === 'asn' ? `AS${r.target}`
                : r.type === 'feed' ? `feed ${r.target}`
                    : r.target;
    return `${acao} ${alvo}`;
}

/**
 * Serviços expostos que a política cobre. A política só filtra o tráfego que
 * chega nesses serviços: sem nenhum, ela não gera nada no roteador.
 */
function servicosCobertos(p: Pick<InboundPolicyDTO, 'scope' | 'applianceId' | 'services'>, servicos: InboundServiceDTO[]) {
    if (p.scope === 'global') return servicos;
    if (p.scope === 'appliance') return servicos.filter((s) => s.applianceId === p.applianceId);
    return servicos.filter((s) => p.services.some((r) => r.tipo === s.tipo && r.id === s.id));
}

const AVISO_SEM_SERVICO =
    'Nenhum redirecionamento de porta ou servidor VPN coberto — a política não tem efeito até existir um. ' +
    '(Winbox/SSH/API do roteador já ficam fechados para a Internet.)';

// ─── EDITOR ───────────────────────────────────────────────────────────────────

function InboundPolicyEditor({
    policy,
    appliances,
    onClose,
}: {
    policy: InboundPolicyDTO | null;
    appliances: ApplianceDTO[];
    onClose: () => void;
}) {
    const [form, setForm] = useState<Form>(policy ? formDe(policy) : FORM_VAZIO);
    const [erro, setErro] = useState<string | null>(null);

    // Todos os serviços: no escopo "servicos" a política pode juntar serviços
    // de várias appliances.
    const queryServicos = useQuery({
        queryKey: ['fw_inbound_services', 'todos'],
        queryFn: async () => {
            const res = await axios.get<{ result: InboundServiceDTO[] }>('/api/firewall/inbound-services', {
                withCredentials: true,
            });
            return res.data.result ?? [];
        },
        enabled: form.scope === 'servicos',
    });
    const nomeAppliance = (id: number) => appliances.find((a) => a.id === id)?.nome ?? `Appliance #${id}`;

    const queryFeeds = useQuery({
        queryKey: ['fw_feeds'],
        queryFn: async () => {
            const res = await axios.get<{ result: FeedDTO[] }>('/api/firewall/feeds', { withCredentials: true });
            return res.data.result ?? [];
        },
    });

    // Agrupado por appliance; dentro do grupo, redirecionamentos antes das VPNs.
    const servicoOptions = (queryServicos.data ?? [])
        .map((s) => ({
            id: `${s.tipo}:${s.id}`,
            label: s.ativo ? s.label : `${s.label} — desativado`,
            group: nomeAppliance(s.applianceId),
            ordem: s.tipo === 'vpn' ? 1 : 0,
        }))
        .sort((a, b) => a.group.localeCompare(b.group) || a.ordem - b.ordem || a.label.localeCompare(b.label))
        .map(({ ordem: _ordem, ...o }) => o);
    const feedOptions = (queryFeeds.data ?? []).map((f) => ({ id: String(f.id), label: f.name }));
    const applianceOptions = appliances.map((a) => ({ id: String(a.id), label: a.nome }));

    const mutSalvar = useMutation(
        async () => {
            const body = {
                name: form.name.trim(),
                description: form.description.trim(),
                scope: form.scope,
                applianceId: form.scope === 'appliance' ? Number(form.applianceId) : null,
                services: form.scope === 'servicos'
                    ? form.services.map((s) => {
                        const [tipo, id] = s.split(':');
                        return { tipo, id: Number(id) };
                    })
                    : [],
                priority: form.priority,
                enabled: form.enabled,
                defaultAction: form.defaultAction,
                rules: form.rules.map((r, i) => ({
                    id: r.id,
                    type: r.type,
                    target: r.type === 'feed' ? '' : r.target.trim(),
                    action: r.action,
                    feedId: r.type === 'feed' ? Number(r.feedId) : null,
                    order: i + 1,
                })),
            };
            return policy
                ? axios.put(`/api/firewall/inbound-policies/${policy.id}`, body, { withCredentials: true })
                : axios.post('/api/firewall/inbound-policies', body, { withCredentials: true });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['fw_inbound_policies']);
                toastSuccess(policy ? 'Política atualizada!' : 'Política criada!');
                onClose();
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao salvar política');
                toastError(message);
            },
        }
    );

    const salvar = () => {
        const e = validar(form);
        setErro(e);
        if (!e) mutSalvar.mutate();
    };

    const setRegra = (i: number, patch: Partial<RegraForm>) =>
        setForm((f) => {
            const rules = [...f.rules];
            rules[i] = { ...rules[i], ...patch };
            return { ...f, rules };
        });

    const moverRegra = (i: number, delta: number) =>
        setForm((f) => {
            const j = i + delta;
            if (j < 0 || j >= f.rules.length) return f;
            const rules = [...f.rules];
            [rules[i], rules[j]] = [rules[j], rules[i]];
            return { ...f, rules };
        });

    return (
        <Modal
            show
            size="lg"
            title={policy ? `Política de entrada — ${policy.name}` : 'Nova política de entrada'}
            onClose={onClose}
            onCancel={onClose}
            onConfirm={salvar}
            confirmProps={{ label: 'Salvar', disabled: mutSalvar.isLoading }}
            cancelProps={{ label: 'Cancelar' }}
        >
            <VBox style={{ gap: '12px' }}>
                {erro && (
                    <div style={{ color: 'var(--color-danger)', fontSize: '13px' }}>{erro}</div>
                )}
                <HBox style={{ gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                        <CampoTexto
                            label="Nome"
                            value={form.name}
                            onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                            placeholder="Ex.: Acesso remoto só do Brasil"
                        />
                    </div>
                    <div style={{ width: '120px' }}>
                        <CampoNumero
                            label="Prioridade"
                            value={form.priority}
                            somentePositivos
                            onChange={(v) => setForm((f) => ({ ...f, priority: v || 1 }))}
                            hint="Menor = antes"
                        />
                    </div>
                </HBox>
                <CampoTexto
                    label="Descrição (opcional)"
                    value={form.description}
                    onChange={(v) => setForm((f) => ({ ...f, description: v }))}
                />

                <CampoSeletor
                    label="Escopo"
                    noSearch
                    value={form.scope}
                    onChange={(v) => setForm((f) => ({ ...f, scope: (v ?? 'servicos') as InboundPolicyScope, services: [] }))}
                    options={SCOPE_OPTIONS}
                />
                {form.scope === 'appliance' && (
                    <CampoSeletor
                        label="Appliance"
                        value={form.applianceId}
                        onChange={(v) => setForm((f) => ({ ...f, applianceId: v, services: [] }))}
                        options={applianceOptions}
                    />
                )}
                {form.scope === 'servicos' && (
                    <CampoMultiSeletor
                        label="Serviços"
                        value={form.services}
                        onChange={(v) => setForm((f) => ({ ...f, services: v }))}
                        options={servicoOptions}
                        placeholder={
                            queryServicos.isLoading ? 'Carregando...'
                                : servicoOptions.length === 0 ? 'Nenhum redirecionamento de porta ou servidor VPN cadastrado'
                                    : 'Escolha redirecionamentos ou servidores VPN'
                        }
                    />
                )}

                {/* Regras */}
                <VBox style={{ gap: '8px' }}>
                    <HBox style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ fontWeight: 500, fontSize: '14px' }}>Regras (a primeira que casar decide)</label>
                        <Botao
                            size="sm"
                            variant="outline-primary"
                            icon={mdiPlus}
                            onClick={() =>
                                setForm((f) => ({
                                    ...f,
                                    rules: [...f.rules, { type: 'country', target: 'BR', action: 'allow', feedId: null }],
                                }))
                            }
                        >
                            Adicionar
                        </Botao>
                    </HBox>
                    {form.rules.length === 0 && (
                        <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: 0 }}>
                            Sem regras — vale só a ação padrão.
                        </p>
                    )}
                    {form.rules.map((r, i) => (
                        <HBox key={r.id ?? `n${i}`} style={{ gap: '8px', alignItems: 'flex-end' }}>
                            <div style={{ width: '160px' }}>
                                <CampoSeletor
                                    label={i === 0 ? 'Ação' : undefined}
                                    noSearch
                                    value={r.action}
                                    onChange={(v) => setRegra(i, { action: (v ?? 'allow') as InboundRuleAction })}
                                    options={RULE_ACTION_OPTIONS}
                                />
                            </div>
                            <div style={{ width: '160px' }}>
                                <CampoSeletor
                                    label={i === 0 ? 'Tipo' : undefined}
                                    noSearch
                                    value={r.type}
                                    onChange={(v) => setRegra(i, { type: (v ?? 'country') as InboundRuleType, target: '', feedId: null })}
                                    options={RULE_TYPE_OPTIONS}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                {r.type === 'country' && (
                                    <CampoSeletor
                                        label={i === 0 ? 'Origem' : undefined}
                                        value={r.target || null}
                                        onChange={(v) => setRegra(i, { target: v ?? '' })}
                                        options={PAISES_OPTIONS}
                                        placeholder="Selecionar país..."
                                    />
                                )}
                                {r.type === 'feed' && (
                                    <CampoSeletor
                                        label={i === 0 ? 'Origem' : undefined}
                                        value={r.feedId}
                                        onChange={(v) => setRegra(i, { feedId: v })}
                                        options={feedOptions}
                                        placeholder="Selecionar feed..."
                                    />
                                )}
                                {(r.type === 'cidr' || r.type === 'asn') && (
                                    <CampoTexto
                                        label={i === 0 ? 'Origem' : undefined}
                                        value={r.target}
                                        onChange={(v) => setRegra(i, { target: v })}
                                        placeholder={r.type === 'asn' ? 'Ex.: 16509' : 'Ex.: 200.1.2.0/24'}
                                    />
                                )}
                            </div>
                            <Botao size="sm" variant="outline-secondary" icon={mdiArrowUp} disabled={i === 0} onClick={() => moverRegra(i, -1)} style={{ marginBottom: '2px' }} />
                            <Botao size="sm" variant="outline-secondary" icon={mdiArrowDown} disabled={i === form.rules.length - 1} onClick={() => moverRegra(i, 1)} style={{ marginBottom: '2px' }} />
                            <Botao
                                size="sm"
                                variant="outline-danger"
                                icon={mdiClose}
                                onClick={() => setForm((f) => ({ ...f, rules: f.rules.filter((_, k) => k !== i) }))}
                                style={{ marginBottom: '2px' }}
                            />
                        </HBox>
                    ))}
                </VBox>

                <CampoSeletor
                    label="Se nenhuma regra casar"
                    noSearch
                    value={form.defaultAction}
                    onChange={(v) => setForm((f) => ({ ...f, defaultAction: (v ?? 'deny') as InboundDefaultAction }))}
                    options={DEFAULT_ACTION_OPTIONS}
                />

                <HBox style={{ gap: '8px', alignItems: 'center' }}>
                    <input
                        type="checkbox"
                        id="inbound-ativo"
                        checked={form.enabled}
                        onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                    />
                    <label htmlFor="inbound-ativo" style={{ cursor: 'pointer' }}>Política ativa</label>
                </HBox>
            </VBox>
        </Modal>
    );
}

// ─── ABA ──────────────────────────────────────────────────────────────────────

export function InboundPoliciesTab() {
    const [editando, setEditando] = useState<InboundPolicyDTO | null>(null);
    const [criando, setCriando] = useState(false);
    const [alternandoId, setAlternandoId] = useState<number | null>(null);

    const queryList = useQuery({
        queryKey: ['fw_inbound_policies', 'todas'],
        queryFn: async () => {
            const res = await axios.get<{ result: InboundPolicyDTO[] }>('/api/firewall/inbound-policies', {
                withCredentials: true,
            });
            return res.data.result ?? [];
        },
    });

    const queryAppliances = useQuery(['appliance-box'], async () => {
        const res = await axios.get<{ result: ApplianceDTO[] }>('/api/appliance', { withCredentials: true });
        return res.data.result;
    });
    const appliances = queryAppliances.data ?? [];

    const queryServicos = useQuery({
        queryKey: ['fw_inbound_services', 'todos'],
        queryFn: async () => {
            const res = await axios.get<{ result: InboundServiceDTO[] }>('/api/firewall/inbound-services', {
                withCredentials: true,
            });
            return res.data.result ?? [];
        },
    });

    const mutExcluir = useMutation(
        async (id: number) => axios.delete(`/api/firewall/inbound-policies/${id}`, { withCredentials: true }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['fw_inbound_policies']);
                toastSuccess('Política removida!');
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao remover política');
                toastError(message);
            },
        }
    );

    const alternar = useCallback(async (p: InboundPolicyDTO) => {
        if (alternandoId) return;
        setAlternandoId(p.id);
        try {
            await axios.patch(`/api/firewall/inbound-policies/${p.id}/toggle`, {}, { withCredentials: true });
            queryClient.invalidateQueries(['fw_inbound_policies']);
        } catch (error) {
            const { message } = extractApiError(error, 'Erro ao alterar estado da política');
            toastError(message);
        } finally {
            setAlternandoId(null);
        }
    }, [alternandoId]);

    const nomeAppliance = (id: number | null) => appliances.find((a) => a.id === id)?.nome ?? `#${id}`;

    const headers: DataTableHeader[] = [
        {
            label: 'Nome',
            width: '30%',
            render: (row: InboundPolicyDTO) => (
                <VBox>
                    <span>{row.name}</span>
                    <HBox style={{ gap: '4px', flexWrap: 'wrap' }}>
                        <Badge variant="info" style={{ fontSize: '10px' }}>
                            {row.scope === 'global' && 'Global'}
                            {row.scope === 'appliance' && `Appliance: ${nomeAppliance(row.applianceId)}`}
                            {row.scope === 'servicos' && (queryServicos.data
                                ? [...new Set(servicosCobertos(row, queryServicos.data).map((s) => nomeAppliance(s.applianceId)))].join(', ') || 'Serviços'
                                : 'Serviços')}
                        </Badge>
                        {queryServicos.data && (() => {
                            const n = servicosCobertos(row, queryServicos.data).length;
                            return n === 0 ? (
                                <span title={AVISO_SEM_SERVICO}>
                                    <Badge variant="warning" style={{ fontSize: '10px' }}>Sem serviço exposto — sem efeito</Badge>
                                </span>
                            ) : (
                                <Badge variant="secondary" style={{ fontSize: '10px' }}>
                                    {n === 1 ? '1 serviço' : `${n} serviços`}
                                </Badge>
                            );
                        })()}
                    </HBox>
                </VBox>
            ),
        },
        { label: 'Prioridade', column: 'priority', width: '100px', alignCenter: true },
        {
            label: 'Regras',
            render: (row: InboundPolicyDTO) =>
                row.rules.length === 0 ? (
                    <span style={{ color: 'var(--color-muted)' }}>—</span>
                ) : (
                    <span style={{ fontSize: '13px' }}>
                        {row.rules.slice(0, 3).map(descreverRegra).join(' · ')}
                        {row.rules.length > 3 ? ` · +${row.rules.length - 3}` : ''}
                    </span>
                ),
        },
        {
            label: 'Sem correspondência',
            width: '150px',
            alignCenter: true,
            render: (row: InboundPolicyDTO) => (
                <Badge variant={row.defaultAction === 'deny' ? 'danger' : 'secondary'}>
                    {row.defaultAction === 'deny' ? 'Bloquear' : 'Seguir'}
                </Badge>
            ),
        },
        {
            label: 'Ativo',
            width: '80px',
            alignCenter: true,
            render: (row: InboundPolicyDTO) => (
                <Botao
                    size="sm"
                    variant="none-primary"
                    icon={row.enabled ? mdiToggleSwitch : mdiToggleSwitchOffOutline}
                    iconProps={{ color: row.enabled ? 'var(--color-success)' : 'var(--color-muted)' }}
                    disabled={alternandoId === row.id}
                    onClick={() => alternar(row)}
                />
            ),
        },
        {
            label: 'Ações',
            width: '120px',
            stickRight: true,
            alignRight: true,
            render: (row: InboundPolicyDTO) => (
                <HBox style={{ gap: '8px', justifyContent: 'flex-end' }}>
                    <Botao size="sm" variant="outline-primary" icon={mdiPencil} onClick={() => setEditando(row)} title="Editar" />
                    <BotaoPerigoso size="sm" variant="outline-danger" icon={mdiTrashCan} onConfirm={() => mutExcluir.mutate(row.id)} />
                </HBox>
            ),
        },
    ];

    return (
        <VBox style={{ gap: '16px', flex: 1, overflow: 'hidden' }}>
            <HBox style={{ justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: 0, flex: 1 }}>
                    Quem, vindo da Internet, alcança os redirecionamentos de porta e servidores VPN. Para cada
                    serviço, as políticas que o cobrem são avaliadas por prioridade; serviço sem política fica
                    aberto a qualquer origem.
                </p>
                <Botao variant="primary" icon={mdiPlus} onClick={() => setCriando(true)}>
                    Nova Política
                </Botao>
            </HBox>

            {queryList.isLoading && (
                <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                    <Icon path={mdiLoading} spin size={2} />
                </HBox>
            )}
            {queryList.isError && (
                <p style={{ color: 'var(--color-danger)' }}>Erro ao carregar políticas. Tente novamente.</p>
            )}
            {!queryList.isLoading && !queryList.isError && (
                <DataTable cabecalhos={headers} linhas={queryList.data ?? []} />
            )}

            {(criando || editando) && (
                <InboundPolicyEditor
                    policy={editando}
                    appliances={appliances}
                    onClose={() => {
                        setCriando(false);
                        setEditando(null);
                    }}
                />
            )}
        </VBox>
    );
}
